import type { InterviewSectionSettings } from "@repo/app-types/entities/interviewSection/settings";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { useState, type ReactNode } from "react";
import { DialogTrigger } from "react-aria-components";
import {
  InterviewSectionPickTypeDialog,
  type InterviewSectionPickTypeOption,
} from "./create/pickTypeDialog.js";

export interface UseInterviewSectionCreateFlowProps {
  isTrainingProject: boolean;
  title: ReactNode;
  description: ReactNode;
  onSectionCreate: (settings: InterviewSectionSettings) => void;
}

export function useInterviewSectionCreateFlow({
  isTrainingProject,
  title,
  description,
  onSectionCreate,
}: UseInterviewSectionCreateFlowProps) {
  const onSectionTypeSelect = async (type: InterviewSectionType) => {
    switch (type) {
      case InterviewSectionType.Questions:
        onSectionCreate({
          type,
        });
        return;

      case InterviewSectionType.RolePlay:
        onSectionCreate({
          type,
          participant_instructions: { content: [] },
          agent_instructions: { content: [] },
        });
        return;

      case InterviewSectionType.KnowledgeTest:
        onSectionCreate({
          type,
          questions: [],
        });
        return;

      case InterviewSectionType.ChatQuiz:
        onSectionCreate({
          type,
          topic: "",
          questions: [],
        });
        return;

      default:
        assertUnreachable(type);
    }
  };

  const collectSectionTypeOptions =
    (): InterviewSectionPickTypeOption<InterviewSectionType>[] => {
      // Training section types
      if (isTrainingProject) {
        return [
          { type: InterviewSectionType.RolePlay },
          { type: InterviewSectionType.KnowledgeTest },
          { type: InterviewSectionType.ChatQuiz },
        ];
      }

      // Recruiting section types
      return [
        // This is implied and can only be added once
        {
          type: InterviewSectionType.Questions,
          disabledReason: "Already added",
        },
        { type: InterviewSectionType.RolePlay },
        { type: InterviewSectionType.KnowledgeTest },
      ];
    };

  const renderPickTypeModal = (
    props: {
      isOpen?: boolean;
      onOpenChange?: (isOpen: boolean) => void;
    } = {},
  ) => {
    return (
      <CenteredModal {...props} isDismissable size={Size.LARGE}>
        <InterviewSectionPickTypeDialog
          title={title}
          description={description}
          options={collectSectionTypeOptions()}
          onSelect={onSectionTypeSelect}
        />
      </CenteredModal>
    );
  };

  const renderInterviewSectionCreateTrigger = (trigger: ReactNode) => {
    return (
      <DialogTrigger>
        {trigger}
        {renderPickTypeModal()}
      </DialogTrigger>
    );
  };

  const [isPickTypeOpen, setIsPickTypeOpen] = useState<boolean>(false);

  const onInterviewSectionCreateClick = () => {
    setIsPickTypeOpen(true);
  };

  const renderInterviewSectionCreateFlow = () => {
    return renderPickTypeModal({
      isOpen: isPickTypeOpen,
      onOpenChange: setIsPickTypeOpen,
    });
  };

  return {
    onInterviewSectionCreateClick,
    renderInterviewSectionCreateTrigger,
    renderInterviewSectionCreateFlow,
  };
}
